import React, { useState, useRef } from 'react';
import { Paperclip, X, Download } from 'lucide-react';

// Labels and icons for each workflow context type
const CONTEXT_META = {
};

const ChatInput = ({ onSendMessage, colorMode, disableClip = false, disabled = false, workflowContextMap = {}, coreDownloadedFiles = [] }) => {
  const [value, setValue] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [attachedContexts, setAttachedContexts] = useState([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef(null);
  const isDark = colorMode === 'dark';

  const handleSend = () => {
    if (value.trim() || selectedFiles.length > 0 || attachedContexts.length > 0 || coreDownloadedFiles.length > 0) {
      const contextData = {};
      attachedContexts.forEach(key => {
        if (workflowContextMap[key]) {
          contextData[key] = workflowContextMap[key];
        }
      });
      onSendMessage(value, selectedFiles, Object.keys(contextData).length > 0 ? contextData : undefined);
      setValue('');
      setSelectedFiles([]);
      setAttachedContexts([]);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    setSelectedFiles(prev => [...prev, ...files]);
  };

  const handleRemoveFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleRemoveContext = (key) => {
    setAttachedContexts(prev => prev.filter(k => k !== key));
  };

  const handleDragOver = (e) => {
    if (disabled) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsDragOver(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    if (disabled) return;
    const contextKey = e.dataTransfer.getData('text/plain');
    if (contextKey && CONTEXT_META[contextKey] && workflowContextMap[contextKey]) {
      setAttachedContexts(prev => prev.includes(contextKey) ? prev : [...prev, contextKey]);
    }
  };

  const hasContent = value.trim() || selectedFiles.length > 0 || attachedContexts.length > 0 || coreDownloadedFiles.length > 0;

  return (
    <div
      style={{
        flexShrink: 0,
        borderTop: isDragOver ? '2px solid #002677' : '1px solid ' + (isDark ? '#1f2937' : '#e5e7eb'),
        background: isDragOver ? (isDark ? '#1a2744' : '#eff6ff') : (isDark ? '#111827' : '#fff'),
        padding: '14px 20px',
        transition: 'background 0.15s, border-color 0.15s',
      }}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <div style={{ width: '100%' }}>
        <div style={{
          textAlign: 'center', fontSize: '11px', color: isDark ? '#6b7280' : '#9ca3af',
          marginBottom: '6px',
        }}>AI generated content may be incorrect</div>

        {/* Attached workflow context chips */}
        {attachedContexts.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '8px' }}>
            {attachedContexts.map(key => {
              const meta = CONTEXT_META[key];
              if (!meta) return null;
              const Icon = meta.icon;
              return (
                <span key={key} style={{
                  display: 'inline-flex', alignItems: 'center', gap: '6px',
                  padding: '5px 12px', borderRadius: '8px', fontSize: '12px', fontWeight: 600,
                  background: isDark ? meta.color + '33' : meta.color + '14',
                  color: isDark ? '#93c5fd' : meta.color,
                  border: '1px solid ' + (isDark ? meta.color + '55' : meta.color + '44'),
                }}>
                  <Icon size={13} />
                  {meta.label}
                  <button onClick={() => handleRemoveContext(key)} style={{
                    background: 'none', border: 'none', cursor: 'pointer',
                    padding: '0', display: 'flex', color: 'inherit', opacity: 0.7, marginLeft: '2px',
                  }}><X size={12} /></button>
                </span>
              );
            })}
          </div>
        )}

        {/* Core downloaded file chips */}
        {coreDownloadedFiles.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '8px' }}>
            {coreDownloadedFiles.map((file, i) => (
              <span key={`core-${i}`} style={{
                display: 'inline-flex', alignItems: 'center', gap: '6px',
                padding: '5px 12px', borderRadius: '8px', fontSize: '12px', fontWeight: 600,
                background: isDark ? '#064e3b33' : '#ecfdf5',
                color: isDark ? '#6ee7b7' : '#047857',
                border: '1px solid ' + (isDark ? '#06543855' : '#a7f3d044'),
              }}>
                <Download size={13} />
                {typeof file === 'string' ? file : file.name}
              </span>
            ))}
          </div>
        )}

        {/* File chips */}
        {selectedFiles.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '10px' }}>
            {selectedFiles.map((file, i) => (
              <span key={i} style={{
                display: 'inline-flex', alignItems: 'center', gap: '6px',
                padding: '4px 10px', borderRadius: '6px', fontSize: '12px',
                background: isDark ? '#1e3a5f' : '#eff6ff',
                color: isDark ? '#93c5fd' : '#1d4ed8',
                border: '1px solid ' + (isDark ? '#1e40af' : '#bfdbfe'),
              }}>
                {file.name}
                <button onClick={() => handleRemoveFile(i)} style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  padding: '0', display: 'flex', color: 'inherit', opacity: 0.7,
                }}><X size={12} /></button>
              </span>
            ))}
          </div>
        )}

        {/* Drag-over hint */}
        {isDragOver && (
          <div style={{
            textAlign: 'center', padding: '8px', marginBottom: '8px',
            borderRadius: '8px', fontSize: '13px', fontWeight: 600,
            color: isDark ? '#93c5fd' : '#002677',
            background: isDark ? 'rgba(0,38,119,0.15)' : 'rgba(0,38,119,0.06)',
            border: '1px dashed ' + (isDark ? '#3b82f6' : '#002677'),
          }}>
            Drop here to attach as context
          </div>
        )}

        <div style={{
          display: 'flex', alignItems: 'center', gap: '10px',
          padding: '8px 12px', borderRadius: '14px',
          border: '1px solid ' + (isDark ? '#374151' : '#d1d5db'),
          background: isDark ? '#1f2937' : '#f9fafb',
        }}>
          <input ref={fileInputRef} type="file" multiple accept=".xlsx,.xls"
            onChange={handleFileSelect} style={{ display: 'none' }} />
          <button onClick={() => !disableClip && !disabled && fileInputRef.current?.click()} style={{
            background: 'none', border: 'none', cursor: (disableClip || disabled) ? 'not-allowed' : 'pointer', padding: '6px',
            display: 'flex', alignItems: 'center', color: (disableClip || disabled) ? (isDark ? '#4b5563' : '#d1d5db') : (isDark ? '#9ca3af' : '#6b7280'),
            borderRadius: '6px', opacity: (disableClip || disabled) ? 0.5 : 1,
            transition: 'opacity 0.15s ease',
          }} title={disableClip ? 'File upload not available' : 'Attach files'}>
            <Paperclip size={18} />
          </button>
          <input type="text" value={value}
            onChange={(e) => !disabled && setValue(e.target.value)}
            onKeyPress={(e) => !disabled && handleKeyPress(e)}
            placeholder={disabled ? 'Viewing history — click Back to Chat or Continue to resume' : attachedContexts.length > 0 ? 'Ask about the attached context...' : 'Type a message...'}
            disabled={disabled}
            style={{
              flex: 1, background: 'none', border: 'none', outline: 'none',
              fontSize: '14px', color: isDark ? '#f3f4f6' : '#111827', padding: '4px 0',
              opacity: disabled ? 0.5 : 1, cursor: disabled ? 'not-allowed' : 'text',
            }}
          />
          <button onClick={handleSend}
            disabled={disabled || !hasContent}
            style={{
              background: hasContent ? '#002677' : (isDark ? '#374151' : '#d1d5db'),
              border: 'none', borderRadius: '999px', padding: '8px 20px',
              cursor: hasContent ? 'pointer' : 'default',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#fff', fontSize: '14px', fontWeight: 600,
              letterSpacing: '0.02em',
              transition: 'background 0.15s',
            }}>
            Send
          </button>
        </div>
        <div style={{
          display: 'flex', justifyContent: 'center', gap: '16px',
          marginTop: '8px', fontSize: '11px', color: isDark ? '#6b7280' : '#9ca3af',
        }}>
          <span>Press Enter to send</span>
          <span>{'\u00b7'}</span>
          <span>Drag workflow buttons here to attach context</span>
        </div>
      </div>
    </div>
  );
};

export default ChatInput;
